import i18n from '@/i18n';
/**
 * 商品库条目 → SHEIN 发品表单（标题 / 主图 / 详情图 / 印花图）。
 * 供商品库批量刊登直调 publish-or-edit 使用。
 */

import {
  carouselUrlsForListingItem,
  detailImageUrlsForListingItem,
  printImageUrlsForListingItem,
  printImageNamesForListingItem,
  appendPrintNamesAfterHyphen,
  isGenericListingImageName,
  isOpaqueStorageBaseName,
  SHEIN_SUPPLIER_CODE_MAX_LEN,
  SHEIN_SUPPLIER_SKU_MAX_LEN,
} from './platformListingItemMedia';
import { resolveListingItemDisplayTitle } from './preferDownstreamListingAssets';
import { isManagedStorageUrl, resolveFetchableImageUrl, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { toManagedCanonicalUploadsUrl } from '@/utils/imageDisplayAccess';
import { findImageSpaceRecordByUrl } from './imageCardMetaActions';
import { convertListingImageToShein } from './convertListingImageToShein';
import { ensureEnglishListingTitle, titleContainsCjk, SHEIN_ENGLISH_TITLE_FAIL_MESSAGE } from './ensureEnglishListingTitle';
import {
  resolveListingSlotSkcIndex,
  extractColorFromImageNames,
  extractColorCodeFromSupplierSku,
  collectTemplateColorLabels,
  formatUnmatchedListingColorMessage,
} from './listingPatternColorMatch';
import { deriveSizeKeysFromSkcList } from '@/shein-platform/utils/sheinSizeChart';
import { getListingProductVideo } from '@/pages/ipe/workbench/utils/listingProductVideo';
import { videoSpaceRecordToUploadFile } from '@/pages/ipe/workbench/utils/videoSpaceLibraryPick';
import { sheinPlatformAPI } from '@/utils/api';
import {
  EMPTY_SHEIN_PRINT_INFRINGEMENT,
  normalizeSheinPrintInfringement,
} from '@/shein-platform/utils/sheinPublishPrintInfringement';
import { getProductMeta, hydrateProductMetaFromItemRaw, resolveListingColorSlots } from './platformListingProductMeta';

export const LISTING_NO_PUBLISHABLE_COLOR_MESSAGE =
  '没有可推送的颜色：请确保已接收颜色能匹配模板主规格，且该颜色含主图';

/** 商品库行 → SHEIN 发品表单印花侵权检测快照 */
export function resolveListingPrintInfringementForShein(listingItem) {
  if (!listingItem || listingItem.id == null) {
    return { ...EMPTY_SHEIN_PRINT_INFRINGEMENT };
  }
  hydrateProductMetaFromItemRaw(listingItem);
  const meta = listingItem.id != null ? (getProductMeta(listingItem.id) || {}) : {};
  const raw = listingItem?.raw && typeof listingItem.raw === 'object' ? listingItem.raw : {};
  const rawRisk = String(raw?.risk || '').trim().toLowerCase();
  const effectiveRisk = (meta.risk && meta.risk !== 'pending')
    ? meta.risk
    : (rawRisk && rawRisk !== 'pending' ? rawRisk : meta.risk);
  const rawAdvice = String(raw?.riskAdvice || '').trim();
  const rawElements = Array.isArray(raw?.riskElements)
    ? raw.riskElements.map((el) => String(el || '').trim()).filter(Boolean)
    : [];
  const effectiveAdvice = meta.riskAdvice
    || rawAdvice
    || (rawElements.length ? rawElements.join('、') : '');
  const effectiveAt = String(meta.assessedAt || raw?.assessedAt || '').trim();
  const assessing = Boolean(meta.assessing);
  const hasResult = Boolean(effectiveAdvice)
    || (effectiveRisk && effectiveRisk !== 'pending' && !assessing);
  if (!hasResult) {
    return { ...EMPTY_SHEIN_PRINT_INFRINGEMENT };
  }
  return normalizeSheinPrintInfringement({
    risk: effectiveRisk || 'pending',
    assessing,
    riskAdvice: effectiveAdvice,
    assessedAt: effectiveAt,
  });
}

function toAbsoluteUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^(data:|blob:|https?:)/i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (typeof window !== 'undefined' && u.startsWith('/')) {
    return `${window.location.origin}${u}`;
  }
  return u;
}

function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]);
  } catch {
    return (raw.split('/').pop() || '').split('?')[0];
  }
}

function readablePrintName(...candidates) {
  for (const raw of candidates) {
    const name = stripImageFileExtension(raw);
    if (name && !isOpaqueStorageBaseName(name) && !isGenericListingImageName(name)) return name;
  }
  return '';
}

async function resolvePrintDisplayName(url, nameHint = '', index = 0) {
  const hinted = readablePrintName(nameHint);
  if (hinted) return hinted;
  const fromUrl = readablePrintName(fileNameFromUrl(url));
  if (fromUrl) return fromUrl;
  try {
    const record = await findImageSpaceRecordByUrl(url);
    const fromSpace = readablePrintName(record?.name, record?.fileName, record?.title);
    if (fromSpace) return fromSpace;
  } catch {
    /* 图片空间回表失败时用占位名，避免把哈希拼进货号 */
  }
  return i18n.t('workbench:ipeUtilError.stampLabel', { index: index + 1 });
}

/** 印花图不传 SHEIN：只留站内地址和名称拼货号，禁止读字节 / upload-pic。 */
function printImageStub(url, nameHint = '', index = 0) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return null;
  const canonical = toManagedCanonicalUploadsUrl(absolute);
  const stored = canonical || absolute;
  const name = readablePrintName(nameHint, fileNameFromUrl(stored)) || `印花${index + 1}`;
  return {
    id: `listing-print-${index}`,
    name,
    previewUrl: stored,
    publicUrl: stored,
    ipeUrl: stored,
    url: stored,
    sheinUrl: '',
    needsSignedPreview: Boolean(canonical) || isManagedStorageUrl(stored),
  };
}

async function imageFromUrl(url, sortHint = 0, nameHint = '', {
  resolvePrintName = false,
  shopId = '',
  imageType = 0,
} = {}) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return null;
  // 发品槽位只存 canonical /uploads/…，展示一律 SignedImage；禁止跨端口绝对 uploads
  const canonical = toManagedCanonicalUploadsUrl(absolute);
  const stored = canonical || absolute;
  let previewUrl = stored;
  if (!stored.startsWith('data:') && !stored.startsWith('blob:') && !canonical) {
    try {
      const fetchable = await resolveFetchableImageUrl(stored);
      if (fetchable) previewUrl = fetchable;
    } catch {
      /* 保留原地址，交由发品页 SignedImage 兜底 */
    }
  }
  const name = resolvePrintName
    ? await resolvePrintDisplayName(stored, nameHint, sortHint)
    : (readablePrintName(nameHint, fileNameFromUrl(stored)) || stripImageFileExtension(nameHint) || stripImageFileExtension(fileNameFromUrl(stored)) || '');
  let sheinUrl = '';
  let convertedIpe = stored;
  let convertedPublic = stored;
  if (shopId && imageType > 0) {
    try {
      const converted = await convertListingImageToShein({
        shopId,
        url: stored,
        imageType,
        nameHint: name,
      });
      if (converted?.sheinUrl) {
        sheinUrl = converted.sheinUrl;
        convertedIpe = converted.ipeUrl || stored;
        convertedPublic = converted.publicUrl || convertedIpe;
        previewUrl = converted.previewUrl || previewUrl;
      }
    } catch {
      /* 转换失败仍保留站内地址，发品提交时后端会再转 */
    }
  }
  return {
    id: `listing-img-${Date.now()}-${sortHint}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    previewUrl,
    publicUrl: convertedPublic,
    ipeUrl: convertedIpe,
    url: convertedIpe,
    sheinUrl,
    needsSignedPreview: Boolean(canonical) || isManagedStorageUrl(convertedIpe),
  };
}

function listingColorSlots(listingItem) {
  const slots = resolveListingColorSlots(listingItem);
  return (Array.isArray(slots) ? slots : [])
    .map((slot) => ({
      patternName: String(slot?.patternName || slot?.mainName || '').trim(),
      mainUrl: String(slot?.mainUrl || slot?.main_url || '').trim(),
      mainName: String(slot?.mainName || slot?.patternName || slot?.main_name || '').trim(),
      colorCode: String(slot?.colorCode || slot?.color_code || '').trim(),
      colorLabel: String(slot?.colorLabel || slot?.color_label || '').trim(),
      detailUrls: Array.isArray(slot?.detailUrls)
        ? slot.detailUrls.map((u) => String(u || '').trim()).filter(Boolean)
        : (Array.isArray(slot?.detail_urls)
          ? slot.detail_urls.map((u) => String(u || '').trim()).filter(Boolean)
          : []),
      detailNames: Array.isArray(slot?.detailNames)
        ? slot.detailNames.map((n) => String(n || '').trim())
        : (Array.isArray(slot?.detail_names) ? slot.detail_names.map((n) => String(n || '').trim()) : []),
    }))
    .filter((slot) => slot.mainUrl);
}

function clearSkcPublishImages(skc) {
  return {
    ...skc,
    mainImage: null,
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    showroomSlots: {},
  };
}

function appendPrintNamesToSkc(skc, printNames) {
  if (!printNames.length || !Array.isArray(skc?.skuRows)) return skc;
  return {
    ...skc,
    skuRows: skc.skuRows.map((sku) => ({
      ...sku,
      supplierSku: appendPrintNamesAfterHyphen(
        sku?.supplierSku,
        printNames,
        { maxLen: SHEIN_SUPPLIER_SKU_MAX_LEN },
      ),
    })),
  };
}

/**
 * 从发品表单印花图槽位提取拼接用名称（去扩展名）。
 * 跳过存储哈希文件名，避免货号拼成 8169071df3… 这类代码。
 * @param {object[]} images
 * @returns {string[]}
 */
export function printNamesFromSheinPrintImages(images) {
  return (Array.isArray(images) ? images : [])
    .map((img, index) => {
      const fromName = readablePrintName(img?.name);
      if (fromName) return fromName;
      const fromUrl = readablePrintName(fileNameFromUrl(
        img?.ipeUrl || img?.publicUrl || img?.url || img?.previewUrl || img?.sheinUrl || '',
      ));
      return fromUrl || `印花${index + 1}`;
    })
    .filter(Boolean);
}

/**
 * 提交前：弱名（哈希）回表图片空间展示名，再写回 printImages / ipePrintImageNames。
 * @param {object} form
 * @returns {Promise<object>}
 */
export async function enrichSheinFormPrintNames(form) {
  if (!form || typeof form !== 'object') return form;
  const images = Array.isArray(form.printImages) ? form.printImages : [];
  if (!images.length) return form;
  const nextImages = await Promise.all(images.map(async (img, index) => {
    if (!img || typeof img !== 'object') return img;
    const current = readablePrintName(img.name);
    if (current) return { ...img, name: current };
    const url = img.ipeUrl || img.publicUrl || img.url || img.previewUrl || '';
    const name = await resolvePrintDisplayName(url, '', index);
    return { ...img, name };
  }));
  const names = printNamesFromSheinPrintImages(nextImages);
  return {
    ...form,
    printImages: nextImages,
    ipePrintImageNames: names,
  };
}

function skcImageUrl(image) {
  if (!image) return '';
  return String(
    image.sheinUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

/**
 * 刊登推送：有主图即可进入自动规则（细节图可选）。
 * @param {object} skc
 * @returns {boolean}
 */
export function skcHasPlatformPublishImages(skc) {
  return Boolean(skcImageUrl(skc?.mainImage));
}

/**
 * 丢掉没有主图的颜色及其尺码，避免把模板里未接收的颜色推到平台。
 * @param {object[]} skcList
 * @returns {object[]}
 */
export function filterSkcsReadyForPlatformPublish(skcList) {
  return (Array.isArray(skcList) ? skcList : []).filter(skcHasPlatformPublishImages);
}

function listingColorSlotHasPublishImages(slot) {
  return Boolean(String(slot?.mainUrl || '').trim());
}

function ensureSkcList(form) {
  const list = Array.isArray(form?.skcList) ? [...form.skcList] : [];
  if (list.length === 0) {
    list.push({
      id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      colorAttributeId: '',
      colorValueId: '',
      colorLabel: '',
      colorIsCustom: false,
      colorExtraValue: '',
      supplierCode: '',
      skcName: '',
      mainImage: null,
      showroomSlots: {},
      detailImages: [],
      squareImage: null,
      colorBlockImage: null,
      skuRows: [],
    });
  }
  return list;
}

/**
 * @param {object} templateForm 商品模板 form 快照
 * @param {object} listingItem 商品库行
 * @param {object} [categoryMeta] { categoryId, productTypeId, categoryPathLabel }
 * @param {{ shopId?: string|number }} [options]
 * @returns {Promise<object>} 合并后的发品 form
 */
export async function applyListingItemToSheinPublishForm(
  templateForm,
  listingItem,
  categoryMeta = {},
  options = {},
) {
  const shopId = String(options.shopId || '').trim();
  const convertOpts = (imageType) => (shopId ? { shopId, imageType } : {});
  const base = templateForm && typeof templateForm === 'object' ? { ...templateForm } : {};
  const sourceTitle = String(
    resolveListingItemDisplayTitle(listingItem) || listingItem?.title || '',
  ).trim();
  const translatedTitle = sourceTitle
    ? await ensureEnglishListingTitle(sourceTitle, { failMessage: SHEIN_ENGLISH_TITLE_FAIL_MESSAGE })
    : '';
  const templateTitle = String(base.title || '').trim();
  const resolvedTitle = String(translatedTitle || '').trim()
    || (!titleContainsCjk(templateTitle) ? templateTitle : '');
  const mainUrl = carouselUrlsForListingItem(listingItem)[0] || '';
  const detailUrls = detailImageUrlsForListingItem(listingItem);
  const printUrls = printImageUrlsForListingItem(listingItem);
  const initialPrintNames = printImageNamesForListingItem(listingItem);
  const printNames = await Promise.all(
    printUrls.map((url, index) => resolvePrintDisplayName(url, initialPrintNames[index] || '', index)),
  );
  const colorSlots = listingColorSlots(listingItem);
  const fallbackPatternName = readablePrintName(
    listingItem?.mainImageNames?.[0],
    listingItem?.mainImageName,
    fileNameFromUrl(mainUrl),
  );
  const slots = colorSlots.length > 0
    ? colorSlots
    : (mainUrl
      ? [{
        patternName: fallbackPatternName,
        mainName: fallbackPatternName,
        mainUrl,
        detailUrls,
        detailNames: [],
      }]
      : []);

  const templateDetailByIndex = ensureSkcList(base).map((skc) => (
    Array.isArray(skc?.detailImages) ? skc.detailImages.filter(Boolean) : []
  ));
  // 先清空模板残留主图，避免未匹配颜色带着旧主图进入发品；细节图按颜色接回
  const skcList = ensureSkcList(base).map((skc) => clearSkcPublishImages({ ...skc }));
  const filledIndexes = new Set();
  const usedIndexes = new Set();

  const fillSkcMedia = async (skcIndex, slot, sortBase) => {
    if (skcIndex < 0 || skcIndex >= skcList.length) return;
    const skc = { ...(skcList[skcIndex] || {}) };
    const mainImage = slot.mainUrl
      ? await imageFromUrl(slot.mainUrl, sortBase, slot.mainName || slot.patternName, convertOpts(1))
      : null;
    if (mainImage) {
      skc.mainImage = mainImage;
      skc.squareImage = null;
      skc.colorBlockImage = null;
    }
    const details = (
      await Promise.all((slot.detailUrls || []).map((url, idx) => imageFromUrl(
        url,
        sortBase + idx + 2,
        slot.detailNames?.[idx] || '',
        convertOpts(2),
      )))
    ).filter(Boolean);
    const seen = new Set(details.map((img) => skcImageUrl(img).toLowerCase()).filter(Boolean));
    (templateDetailByIndex[skcIndex] || []).forEach((img) => {
      const key = skcImageUrl(img).toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      details.push(img);
    });
    skc.detailImages = details;
    if (slot.colorCode) skc.colorCode = slot.colorCode;
    if (slot.colorLabel) {
      skc.colorLabel = slot.colorLabel;
    }
    const skuColorCode = extractColorCodeFromSupplierSku(
      (Array.isArray(skc.skuRows) ? skc.skuRows : [])[0]?.supplierSku,
    );
    if (!skc.colorCode && skuColorCode) {
      skc.colorCode = skuColorCode;
    }
    if (!skc.colorCode || !skc.colorLabel) {
      const fromNames = extractColorFromImageNames([
        slot.mainName || slot.patternName,
        ...((slot.detailUrls || []).map((url, idx) => (
          String(slot.detailNames?.[idx] || fileNameFromUrl(url) || '').trim()
        ))),
      ]);
      if (!skc.colorCode && fromNames.colorCode) skc.colorCode = fromNames.colorCode;
      if (!skc.colorLabel && fromNames.colorLabel) skc.colorLabel = fromNames.colorLabel;
    }
    skcList[skcIndex] = appendPrintNamesToSkc(skc, printNames);
    if (skcHasPlatformPublishImages(skcList[skcIndex])) {
      filledIndexes.add(skcIndex);
      usedIndexes.add(skcIndex);
    }
  };

  const unmatchedColorMessages = [];
  const skippedColors = [];
  const templateColors = collectTemplateColorLabels(skcList);
  for (let i = 0; i < slots.length; i += 1) {
    const slot = slots[i];
    if (!listingColorSlotHasPublishImages(slot)) continue;
    let skcIndex = resolveListingSlotSkcIndex(skcList, slot, { usedIndexes });
    // 无显式色槽时：仅单色模板可回退首个 SKC；多色必须命中，禁止串色
    if (skcIndex < 0 && colorSlots.length === 0 && skcList.length === 1) skcIndex = 0;
    if (skcIndex >= 0 && usedIndexes.has(skcIndex)) {
      skcIndex = -1;
    }
    if (skcIndex < 0) {
      // 模板没有该颜色：跳过这一色（与商品库刊登一致），其余颜色照常发品；
      // 只有全部颜色都未命中时才报错，避免整条商品被一个多余色拖死。
      const message = formatUnmatchedListingColorMessage(slot, templateColors);
      if (!unmatchedColorMessages.includes(message)) unmatchedColorMessages.push(message);
      skippedColors.push({
        colorLabel: String(slot.colorLabel || '').trim(),
        colorCode: String(slot.colorCode || '').trim(),
        mainName: String(slot.mainName || slot.patternName || '').trim(),
        message,
      });
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    await fillSkcMedia(skcIndex, slot, (i + 1) * 20);
  }
  if (filledIndexes.size === 0 && unmatchedColorMessages.length > 0) {
    throw new Error(unmatchedColorMessages[0]);
  }

  const supplierCode = appendPrintNamesAfterHyphen(
    base.supplierCode,
    printNames,
    { maxLen: SHEIN_SUPPLIER_CODE_MAX_LEN },
  );

  const printImages = printUrls.map((url, idx) => printImageStub(
    url,
    printNames[idx] || '',
    idx,
  )).filter(Boolean);

  const nextSkcList = filterSkcsReadyForPlatformPublish(
    skcList.filter((_, index) => filledIndexes.has(index)),
  );

  let sheinVideos = Array.isArray(base.sheinVideos) ? base.sheinVideos.filter(Boolean) : [];
  if (!sheinVideos.length) {
    const listingVideo = getListingProductVideo(listingItem);
    if (listingVideo) {
      if (listingVideo.videoUrl || listingVideo.sheinUrl) {
        sheinVideos = [{
          videoUrl: listingVideo.videoUrl || listingVideo.sheinUrl,
          sheinUrl: listingVideo.sheinUrl || listingVideo.videoUrl,
          ipeUrl: listingVideo.ipeUrl || listingVideo.url || '',
          taskId: listingVideo.taskId || '',
          name: listingVideo.name || '商品视频',
        }];
      } else if (shopId && (listingVideo.ipeUrl || listingVideo.url)) {
        try {
          const file = await videoSpaceRecordToUploadFile({
            id: listingVideo.videoSpaceId,
            name: listingVideo.name || 'video.mp4',
            outputVideoUrl: listingVideo.ipeUrl || listingVideo.url,
            _playUrl: listingVideo.ipeUrl || listingVideo.url,
          }, listingVideo.name || 'video.mp4');
          const res = await sheinPlatformAPI.uploadProductVideo(shopId, file, {
            fromVideoSpace: Boolean(listingVideo.videoSpaceId),
          });
          const videoUrl = String(res?.videoUrl || res?.sheinUrl || '').trim();
          if (videoUrl) {
            sheinVideos = [{
              videoUrl,
              sheinUrl: res?.sheinUrl || videoUrl,
              ipeUrl: res?.ipeUrl || listingVideo.ipeUrl || listingVideo.url || '',
              taskId: res?.taskId || '',
              name: listingVideo.name || '商品视频',
            }];
          }
        } catch {
          /* 视频可选 */
        }
      }
    }
  }

  return {
    ...base,
    title: resolvedTitle,
    titleLanguage: resolvedTitle ? 'en' : (base.titleLanguage || 'en'),
    supplierCode,
    printImages,
    ipePrintImageNames: printNames,
    ipePrintInfringement: resolveListingPrintInfringementForShein(listingItem),
    categoryId: categoryMeta.categoryId || base.categoryId || '',
    productTypeId: categoryMeta.productTypeId || base.productTypeId || '',
    categoryPathLabel: categoryMeta.categoryPathLabel || base.categoryPathLabel || '',
    skcList: nextSkcList,
    selectedSizeIds: nextSkcList.length
      ? deriveSizeKeysFromSkcList(nextSkcList)
      : [],
    // 模板里不存在的颜色被跳过：仅用于刊登日志/提示，不进入发品 payload
    ipeSkippedListingColors: skippedColors,
    sheinVideos,
  };
}

/**
 * 保存/提交前：把印花图名称接到商家货号与各 SKU 的「-」后面（多张用 +）。
 * 优先用传入 names，其次从表单印花图槽位推导，最后回退 ipePrintImageNames。
 * @param {object} form
 * @param {string[]} [printNames]
 */
export function applyPrintNamesToSheinForm(form, printNames) {
  const fromImages = printNamesFromSheinPrintImages(form?.printImages);
  const fallbackNames = Array.isArray(form?.ipePrintImageNames)
    ? form.ipePrintImageNames.map((n) => readablePrintName(n)).filter(Boolean)
    : [];
  const names = Array.isArray(printNames) && printNames.length
    ? printNames.map((n) => readablePrintName(n) || String(n || '').trim()).filter(Boolean)
    : (fromImages.length ? fromImages : fallbackNames);
  if (!form || !names.length) return form;
  const skcList = Array.isArray(form.skcList)
    ? form.skcList.map((skc) => ({
      ...skc,
      skuRows: Array.isArray(skc?.skuRows)
        ? skc.skuRows.map((sku) => ({
          ...sku,
          supplierSku: appendPrintNamesAfterHyphen(
            sku?.supplierSku,
            names,
            { maxLen: SHEIN_SUPPLIER_SKU_MAX_LEN },
          ),
        }))
        : skc?.skuRows,
    }))
    : form.skcList;
  return {
    ...form,
    supplierCode: appendPrintNamesAfterHyphen(
      form.supplierCode,
      names,
      { maxLen: SHEIN_SUPPLIER_CODE_MAX_LEN },
    ),
    ipePrintImageNames: names,
    skcList,
  };
}
